import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  Card,
  CardWord,
  CheckWordsBulkResponse,
  BulkCreateCardsResponse,
  CardsListParams,
  CreateCardData,
  UpdateCardData,
  CardsPaginatedResponse,
  ProgressHistoryParams,
  ProgressHistoryResponse,
  Clue,
} from '@/types'

interface ApiCardResponse {
  success: boolean
  data: Card
  message?: string
}

interface ApiCheckWordResponse {
  success: boolean
  data: {
    found: boolean
    card_exists?: boolean
    word?: CardWord
  }
  message?: string
  error?: string
}

interface ApiCheckBulkResponse {
  success: boolean
  data: {
    found: CardWord[]
    not_found: string[]
  }
}

export const cardsApi = {
  getCards: async (
    params?: CardsListParams
  ): Promise<CardsPaginatedResponse> => {
    const response = await apiClient.get<CardsPaginatedResponse>(
      API_ENDPOINTS.CARDS,
      { params }
    )
    return response.data
  },

  checkWord: async (data: { text: string; user_id?: number }): Promise<{
    found: boolean
    card_exists?: boolean
    word?: CardWord
    error?: string
    message?: string
  }> => {
    const response = await apiClient.post<ApiCheckWordResponse>(
      `${API_ENDPOINTS.CARDS}/check`,
      data
    )
    const d = response.data.data
    return {
      found: d.found,
      card_exists: d.card_exists,
      word: d.word,
    }
  },

  createCard: async (data: CreateCardData): Promise<Card> => {
    const response = await apiClient.post<ApiCardResponse>(
      API_ENDPOINTS.CARDS,
      data
    )
    return response.data.data
  },

  checkWords: async (data: {
    texts: string[]
    user_id?: number
  }): Promise<CheckWordsBulkResponse> => {
    const response = await apiClient.post<ApiCheckBulkResponse>(
      `${API_ENDPOINTS.CARDS}/check-bulk`,
      data
    )
    return response.data.data
  },

  bulkCreateCards: async (data: {
    words: Array<{
      text: string
      attributes?: Array<{
        key: string
        value: string
        description?: string
      }>
    }>,
    user_id?: number
  }): Promise<BulkCreateCardsResponse['data']> => {
    const response = await apiClient.post<{
      success: boolean
      message?: string
      data: BulkCreateCardsResponse['data']
    }>(`${API_ENDPOINTS.CARDS}/bulk`, data)
    return response.data.data
  },

  getCard: async (id: number): Promise<Card> => {
    const response = await apiClient.get<ApiCardResponse>(
      `${API_ENDPOINTS.CARDS}/${id}`
    )
    return response.data.data
  },

  updateCard: async (id: number, data: UpdateCardData): Promise<Card> => {
    const response = await apiClient.put<ApiCardResponse>(
      `${API_ENDPOINTS.CARDS}/${id}`,
      data
    )
    return response.data.data
  },

  deleteCard: async (id: number): Promise<void> => {
    await apiClient.delete(`${API_ENDPOINTS.CARDS}/${id}`)
  },

  addClue: async (cardId: number, text: string): Promise<Clue> => {
    const response = await apiClient.post<{ success: boolean; data: Clue }>(
      `${API_ENDPOINTS.CARDS}/${cardId}/clues`,
      { text }
    )
    return response.data.data
  },

  deleteClue: async (cardId: number, clueId: number): Promise<void> => {
    await apiClient.delete(
      `${API_ENDPOINTS.CARDS}/${cardId}/clues/${clueId}`
    )
  },

  getWordClues: async (wordId: number): Promise<Clue[]> => {
    const response = await apiClient.get<{ success?: boolean; data: Clue[] }>(
      `/words/${wordId}/clues`
    )
    return response.data.data ?? []
  },

  getCardProgressHistory: async (
    cardId: number,
    params?: ProgressHistoryParams
  ): Promise<ProgressHistoryResponse> => {
    const response = await apiClient.get<ProgressHistoryResponse>(
      `${API_ENDPOINTS.CARDS}/${cardId}/progress-history`,
      { params }
    )
    return response.data
  },
}
